import { GroupType, Responding } from "./assessmentTypes";
import { PaginatedResponse } from "./globalTypes";
import { FormsType } from "./reportTypes";
import { User } from "./userTypes";

// organization
export interface Organization {
  id: number;
  name: string;
  tag: Tag | null;
  tagId?: number | null;
  province: Province | null;
  provinceId?: number | null;
  createdAt?: string;
  needVerifyCompanies: boolean;
  industrial: boolean;
  users?: UserAssignment[];
  parents?: Organization[];
  children?: Organization[];
  formId?: number | null;
  form?: FormsType | null;
  provinces?: Province[];
  industrials?: Industry[];
}

export type OrganizationResponse = PaginatedResponse<Organization>;

export interface UserAssignment {
  userId: number;
  provinceIds: number[];
  industrialIds: number[];
  user?: User;
  provinces?: Province[];
  industrials?: Industry[];
  default?: {
    label: string;
    value: string | number;
  };
}

// province
export type Province = {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
};

// tag
export type Tag = {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
};

// export types
export enum ExportType {
  TON = "TON",
  KILOGRAM = "KILOGRAM",
  SQUARE_METER = "SQUARE_METER",
  CUBIC_METER = "CUBIC_METER",
  LITER = "LITER",
  NUMBER = "NUMBER",
  PIECE = "PIECE",
  DEVICE = "DEVICE",
  DOSE = "DOSE",
  MEGAWATT_HOUR = "MEGAWATT_HOUR",
  OTHER = "OTHER",
}

// company
export interface Company {
  id: number;
  name: string;
  logo: any;
  nationalId: string;
  createdAt?: string;
  updatedAt?: string;
  user?: User;
  userId?: number;
  organization?: Organization;
  organizationId?: number;
  industrialId: number;
  industrial?: Organization;
  provinceId?: number | null;
  province?: Province;
  ecosystemId?: number | null;
  companyType?: CompanyType;
  foundationYear?: string;
  licenseNumber?: string | null;
  licenseDate?: string;
  activityType?: ActivityType;
  activityDescription?: string;
  totalStaffCount?: TotalStaffCount;
  isKnowledgeBased?: boolean;
  knowledgeBasedYear?: number | null;
  companyStage?: CompanyStage | null;
  isListed?: boolean;
  hasExport?: boolean;
  exportAmount?: number | null;
  exportVolume?: number | null;
  industrySectorId?: number;
  industrySectorOther?: string;
  industrySector?: Industry;
  responding?: Responding[];
  isTechnologist?: boolean;
  technologistLicenseNumber?: number;
  technologistLicenseDate?: string;
  companyTypeOther?: string;
  industrialOther?: string;
  meta: {
    [key: string]: any;
  };
  // arrays
  organizationChartImage?: File[];
  documents?: File[];
  customers: any[];
  suppliers: any[];
  people?: any[];
  processSteps: {
    title: string;
    order?: number;
    description: string;
    image?: any;
    _tempId?: string;
  }[];
  products: {
    name: string;
    price: number;
    description?: string;
  }[];
}

export type CompanyResponse = PaginatedResponse<Company>;

export interface TotalStaffCount {
  total: number;
  production: number;
  companySize: GroupType;
  administrative: number;
}

// news
export type CompanyMode = "create" | "edit" | "view";

export type CompanyFormValues = {
  name: string;
  logo: any;
  nationalId: string;
  meta: {
    [key: string]: any;
  };

  organization?: Organization;
  industrial?: Organization;

  userId?: number;
  user?: User;
  ecosystemId?: number;
  organizationId?: number;
  industrialId?: number;
  companyType: CompanyType;
  companyTypeOther?: string;

  foundationYear?: string;
  provinceId?: number;
  licenseNumber?: number;
  licenseDate?: string;
  activityType: ActivityType;
  activityDescription?: string;
  industrySectorId?: number;
  industrySectorOther?: string;
  companyStage?: CompanyStage | null;
  isKnowledgeBased?: boolean;
  knowledgeBasedYear?: string;
  isListed?: boolean;
  industrialOther?: string;
  isTechnologist?: boolean;
  technologistLicenseNumber?: number;
  technologistLicenseDate?: string;

  totalStaffCount?: {
    production?: number;
    administrative?: number;
  };
  exportAmount?: number;
  exportVolume?: number;

  // arrays
  organizationChartImage?: File[];
  documents?: File[];
  customers: any[];
  suppliers: any[];
  people?: any[];
  processSteps: {
    title: string;
    order: number;
  }[];
  products: {
    name: string;
    price: number;
    description?: string;
  }[];
};

export enum CompanyType {
  PUBLIC = "PUBLIC",
  PRIVATE = "PRIVATE",
  NON_COMMERCIAL = "NON_COMMERCIAL",
  NON_COMMERCIAL_PROFIT = "NON_COMMERCIAL_PROFIT",
  INSTITUTE = "INSTITUTE",
  OTHER = "OTHER",
}

export enum ActivityType {
  PRODUCTION = "PRODUCTION",
  SERVICE = "SERVICE",
  BUSINESS = "BUSINESS",
}

export enum CompanyStage {
  PRE_GROWTH = "PRE_GROWTH",
  GROWTH = "GROWTH",
  INSTITUTION = "INSTITUTION",
}

export interface Product {
  name: string;
  price?: number;
  description?: string;
}

export interface Supplier {
  name: string;
  phone?: string;
  address?: string;
}

export interface Customer {
  name: string;
  phone?: string;
  email?: string;
}

// industry
export type Industry = {
  id: number;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  key: string;
  index: number;
};

// verify
export interface VerifyCompanyRequest {
  nationalId: string;
  organizationId: number;
}

export interface VerifyCompanyResponse {
  isValid: boolean;
  message?: string;
  data?: {
    companyName?: string;
    organizationName?: string;
    status?: string;
  };
}

export interface CompanyVerification {
  id: number;
  nationalId: string;
  organizationId: number;
  createdAt: string;
  updatedAt: string;
  organization: Organization;
  company?: Company | null;
}

export type CompanyVerificationResponse =
  PaginatedResponse<CompanyVerification>;
